import React, { FC } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Fade,
  CircularProgress,
  Alert,
} from '@mui/material'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import CloseIcon from '@mui/icons-material/Close'
import styles from './ImportDialog.module.scss'
import { ImportDialogProps } from './ImportDialog.types'
import { useImportDialogLogic } from './ImportDialog.logic'

const ImportDialog: FC<ImportDialogProps> = ({
  open,
  onClose,
  onFileSelect,
  onSubmit,
  selectedFile,
  loading = false,
  error = null,
  title = 'Import Data',
  description = 'Drag & Drop Excel or CSV file here, or browse to select.',
}) => {
  const {
    fileInputRef,
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileChange,
    handleClearFile,
  } = useImportDialogLogic(onFileSelect)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box className={styles.dialogTitle}>
          <Typography variant="h4">{title}</Typography>
          <IconButton onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          className={`${styles.dragDropArea}${
            isDragging ? ` ${styles.dragging}` : ''
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {loading && (
            <Fade in={loading}>
              <Box className={styles.loadingOverlay}>
                <Box className={styles.loadingContent}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Processing file...
                  </Typography>
                </Box>
              </Box>
            </Fade>
          )}
          <FileUploadIcon className={styles.uploadIcon} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            {description}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Supported formats: .xlsx, .xls, .csv
          </Typography>
          <Button
            variant="contained"
            size="medium"
            component="label"
            sx={{ mt: 2 }}
            disabled={loading}
          >
            Browse Files
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls, .csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={loading}
            />
          </Button>
        </Box>
        {selectedFile && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Chip
              label={selectedFile.name}
              color="primary"
              variant="outlined"
              onDelete={!loading ? handleClearFile : undefined}
              className={styles.fileChip}
            />
          </Box>
        )}
        {error && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          size="medium"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          color="primary"
          variant="contained"
          size="medium"
          disabled={loading || !selectedFile}
          className={styles.importButton}
        >
          {loading ? (
            <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
          ) : null}
          Import
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImportDialog
